import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { resolveImg } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Trash2, RefreshCw, Play, ImagePlus } from "lucide-react";

const MED_FIELDS = ["name", "subtitle", "theme", "short_line", "headline", "description", "price_inr", "duration", "language", "status", "display_order", "practice_points", "closing_heading"];
const COL_FIELDS = ["name", "description", "price_inr", "combined_value_inr", "status"];

function Field({ label, k, form, upd, type = "text", area, hint }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {area ? <Textarea rows={3} value={form[k] ?? ""} onChange={e => upd(k, e.target.value)} data-testid={`med-field-${k}`} />
        : <Input type={type} value={form[k] ?? ""} onChange={e => upd(k, e.target.value)} data-testid={`med-field-${k}`} />}
      {hint && <p className="text-[11px] text-[#9C928D] mt-1">{hint}</p>}
    </div>
  );
}

function FileButton({ label, icon: I, accept, onFile, busy, testid }) {
  return (
    <label className={`btn-ghost text-xs !px-4 !py-2 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid={`${testid}-label`}>
      <I size={14} className="mr-2" />{label}
      <input type="file" accept={accept} className="hidden" data-testid={testid} onChange={e => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
    </label>
  );
}

function PreviewVideoPanel({ item, onChange }) {
  const [pct, setPct] = useState(null);
  const [dur, setDur] = useState(null);
  const src = resolveImg(item.preview_video_url);
  const upload = async (file) => {
    if (!file) return;
    setPct(0);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/meditations/${item.id}/preview-video`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: e => setPct(Math.round((e.loaded / (e.total || 1)) * 100)) });
      onChange(data); toast.success("Preview video saved");
    } catch (e) { toast.error(formatErr(e)); } finally { setPct(null); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this preview video? The website will show 'Preview coming soon'.")) return;
    try { const { data } = await api.delete(`/admin/meditations/${item.id}/preview-video`); onChange(data); toast.success("Preview video removed"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="rounded-2xl border border-dashed border-[#D9CFC2] bg-[#FBF8F3] p-5" data-testid="preview-video-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-medium text-sm">20-second Preview Video</div>
          <p className="text-[11px] text-[#9C928D]">MP4 / WEBM / MOV · max 100MB · shown via "Play Preview" on the website</p>
        </div>
        <span className={`badge-soft ${src ? "bg-[#D4E4D3] text-[#2F4F2F]" : "bg-[#F3EFEA] text-[#5C5955]"}`} data-testid="preview-video-status">{src ? "Preview assigned" : "No preview yet"}</span>
      </div>
      {src ? (
        <div className="mt-4">
          <video key={src} src={src} controls playsInline preload="metadata" className="w-full max-w-md rounded-xl bg-black" data-testid="admin-preview-player"
            onLoadedMetadata={e => setDur(e.currentTarget.duration)} />
          {dur != null && <p className={`text-[11px] mt-1 ${Math.abs(dur - 20) > 1.5 ? "text-amber-700" : "text-[#7E736E]"}`} data-testid="preview-duration">Length: {dur.toFixed(1)}s{Math.abs(dur - 20) > 1.5 ? " — expected about 20 seconds" : ""}</p>}
        </div>
      ) : <div className="mt-4 text-sm text-[#9C928D]">No preview uploaded. Visitors see "Preview coming soon".</div>}
      {pct != null && <div className="mt-3 h-1.5 rounded-full bg-[#EBE5D9] overflow-hidden"><div className="h-full bg-[#E07A5F] transition-[width]" style={{ width: `${pct}%` }} /></div>}
      <div className="mt-4 flex gap-2 flex-wrap">
        <FileButton label={pct != null ? `Uploading ${pct}%` : src ? "Replace Preview Video" : "Upload Preview Video"} icon={src ? RefreshCw : Upload}
          accept="video/mp4,video/webm,video/quicktime,.mov,.m4v" onFile={upload} busy={pct != null} testid="preview-video-upload" />
        {src && <button type="button" className="btn-ghost text-xs !px-4 !py-2" data-testid="preview-video-play"
          onClick={() => { const v = document.querySelector('[data-testid="admin-preview-player"]'); if (v) { v.currentTime = 0; v.play(); } }}><Play size={14} className="mr-2" />Play Current Preview</button>}
        {src && <button type="button" onClick={remove} className="btn-ghost text-xs !px-4 !py-2 !text-red-700" data-testid="preview-video-remove"><Trash2 size={14} className="mr-2" />Remove Preview Video</button>}
      </div>
    </div>
  );
}

function ImagePanel({ item, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/meditations/${item.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data); toast.success("Image saved");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const clear = async () => {
    try { const { data } = await api.patch(`/admin/meditations/${item.id}`, { image_url: "" }); onChange(data); toast.success("Image removed — theme word shown"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="rounded-2xl border border-[#EBE5D9] p-5" data-testid="image-panel">
      <div className="font-medium text-sm">Thumbnail / Product Image</div>
      <p className="text-[11px] text-[#9C928D]">JPEG / PNG / WEBP · max 8MB</p>
      {item.image_url ? <img src={resolveImg(item.image_url)} alt="" className="mt-3 rounded-xl max-h-40 object-cover" data-testid="image-preview" />
        : <div className="mt-3 text-sm text-[#9C928D]">No image — a soft themed placeholder is shown.</div>}
      <div className="mt-3 flex gap-2 flex-wrap">
        <FileButton label={busy ? "Uploading…" : item.image_url ? "Replace Image" : "Upload Image"} icon={ImagePlus} accept="image/jpeg,image/png,image/webp" onFile={upload} busy={busy} testid="image-upload" />
        {item.image_url && <button type="button" onClick={clear} className="btn-ghost text-xs !px-4 !py-2 !text-red-700" data-testid="image-remove">Remove Image</button>}
      </div>
    </div>
  );
}

function Editor({ item, onSaved }) {
  const isCol = item.kind === "collection";
  const [form, setForm] = useState(item);
  useEffect(() => setForm(item), [item]);
  const upd = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const save = async () => {
    const payload = Object.fromEntries((isCol ? COL_FIELDS : MED_FIELDS).map(k => [k, form[k]]));
    try { const { data } = await api.patch(`/admin/meditations/${item.id}`, payload); onSaved(data); toast.success("Saved — website updated"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const p = { form, upd };
  return (
    <div className="grid xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-6" data-testid={`editor-${item.slug}`}>
      <div className="soft-card !p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={isCol ? "Collection Name" : "Product Name"} k="name" {...p} />
          {!isCol && <Field label="Subtitle" k="subtitle" {...p} />}
        </div>
        {!isCol && <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Theme label (e.g. PRESENCE)" k="theme" {...p} hint="Used in the theme tag and in CTAs like 'Choose Presence'" />
          <Field label="Display Order" k="display_order" type="number" {...p} />
        </div>}
        {!isCol && <Field label="Short positioning line" k="short_line" area {...p} />}
        {!isCol && <Field label="Positioning headline" k="headline" area {...p} />}
        <Field label={isCol ? "Short description" : "Product description"} k="description" area {...p} />
        {!isCol && <div>
          <Label className="text-xs">Product page — "With {form.name}, practise:" (one line each)</Label>
          <Textarea rows={3} value={Array.isArray(form.practice_points) ? form.practice_points.join("\n") : (form.practice_points || "")}
            onChange={e => upd("practice_points", e.target.value.split("\n"))} data-testid="med-field-practice_points" />
        </div>}
        {!isCol && <Field label="Product page — closing heading" k="closing_heading" {...p} hint="e.g. Return to Joy." />}
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label={isCol ? "Selling Price India (₹)" : "Price India (₹)"} k="price_inr" type="number" {...p} />
          {isCol ? <Field label="Individual Combined Value (₹)" k="combined_value_inr" type="number" {...p} />
            : <><Field label="Duration" k="duration" {...p} hint="e.g. 15 min" /><Field label="Language" k="language" {...p} /></>}
        </div>
        <div className="sm:w-1/3">
          <Label className="text-xs">Status</Label>
          <Select value={form.status} onValueChange={v => upd("status", v)}>
            <SelectTrigger data-testid="med-field-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
          </Select>
        </div>
        {!isCol && <p className="text-xs text-[#7E736E]" data-testid="meta-preview">Website shows: ₹{Number(form.price_inr || 0).toLocaleString("en-IN")} · {form.duration} · {form.language}</p>}
        <button onClick={save} className="btn-primary" data-testid="med-save">Save changes</button>
      </div>
      <div className="space-y-5">
        {!isCol && <PreviewVideoPanel item={item} onChange={onSaved} />}
        <ImagePanel item={item} onChange={onSaved} />
      </div>
    </div>
  );
}

function R2StatusPanel() {
  const [s, setS] = useState(null);
  const load = () => api.get("/admin/r2-status").then(r => setS(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);
  if (!s) return null;
  const missing = Object.entries(s.settings).filter(([k, v]) => !v && k !== "region" && k !== "prefix").map(([k]) => `R2_${k.toUpperCase()}`);
  return (
    <div className="soft-card !p-5" data-testid="r2-status-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-medium text-sm">Full meditation videos — private Cloudflare R2</div>
          <p className="text-[11px] text-[#9C928D]">Bucket <b>{s.bucket || "—"}</b> stays private. Buyers get {Math.round(s.url_ttl_seconds / 60)}-minute signed links only after verified payment.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`badge-soft ${s.configured ? "bg-[#D4E4D3] text-[#2F4F2F]" : "bg-[#FBF3E4] text-[#7A5C1F]"}`} data-testid="r2-configured">{s.configured ? "Connected settings present" : "Setup incomplete"}</span>
          <button onClick={load} className="btn-ghost text-xs !px-3 !py-1.5" data-testid="r2-refresh"><RefreshCw size={12} className="mr-1" />Check files</button>
        </div>
      </div>
      {!s.configured && <p className="mt-3 text-xs text-[#7A5C1F]" data-testid="r2-missing">Missing secret(s): {missing.join(", ")}</p>}
      <ul className="mt-3 grid sm:grid-cols-2 gap-2 text-xs">
        {s.files.map(f => (
          <li key={f.slug} className="rounded-xl border border-[#EBE5D9] px-3 py-2 flex items-center justify-between gap-2" data-testid={`r2-file-${f.slug}`}>
            <code className="truncate">{f.key}</code>
            <span className={f.exists ? "text-[#2F4F2F]" : f.error ? "text-red-700" : "text-[#9C928D]"}>{f.exists ? `Ready${f.size ? ` · ${(f.size / 1048576).toFixed(0)}MB` : ""}` : f.error || "Not uploaded yet"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MeditationsAdminPage() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/admin/meditations").then(({ data }) => { setItems(data); setSel(s => s || data[0]?.id); }); }, []);
  const onSaved = (d) => setItems(list => list.map(x => (x.id === d.id ? d : x)));
  const cur = items.find(x => x.id === sel);
  return (
    <div className="space-y-6 fade-up" data-testid="admin-meditations">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl">Digital Meditations</h1>
          <p className="text-sm text-[#7E736E]">One-time purchase products shown on the Meditations Collection page. Changes apply to the page instantly.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {cur?.kind === "meditation" && <a href={`/meditations/${cur.slug}`} target="_blank" rel="noreferrer" className="btn-ghost text-sm" data-testid="open-product-page">Open Product Page</a>}
          <a href="/meditations" target="_blank" rel="noreferrer" className="btn-ghost text-sm" data-testid="open-collection-page">Open Collection Page</a>
        </div>
      </div>
      <R2StatusPanel />
      <div className="flex gap-2 flex-wrap" data-testid="med-tabs">
        {items.map(x => (
          <button key={x.id} onClick={() => setSel(x.id)} data-testid={`med-tab-${x.slug}`}
            className={`rounded-full px-4 py-2 text-sm border transition-colors ${sel === x.id ? "bg-[#4A3B32] text-white border-[#4A3B32]" : "bg-white border-[#EBE5D9] text-[#4A3B32] hover:bg-[#F3EFEA]"}`}>
            {x.kind === "collection" ? "Complete Collection" : x.name}
            <span className="ml-2 opacity-70">₹{x.price_inr}</span>
            {x.status !== "active" && <span className="ml-2 text-[10px] uppercase">inactive</span>}
          </button>
        ))}
      </div>
      {cur && <Editor item={cur} onSaved={onSaved} />}
    </div>
  );
}
